import {
  ENDPOINT_MASTER_APPRENTICES_DISTRICT,
  ENDPOINT_MASTER_APPRENTICES_DISTRICT_RATES,
  ENDPOINT_MASTER_APPRENTICES_RATES,
  ENDPOINT_MASTER_APPRENTICES_REGION,
  ENDPOINT_MASTER_APPRENTICES_REGION_RATES,
  ENDPOINT_MASTER_APPRENTICES_REPUBLIC,
} from '@/common/constants';
import { HttpApi } from '@/common/http';
import { cleanObject } from '@/utils/clean-object';
import { create } from 'zustand';
import { REAGION_SOATO } from '@/common/constants/regions';
import {
  MasterApprenticesDistrictResponse,
  MasterApprenticesRepublicResponse,
  type MasterApprentices,
  type MasterApprenticesRegionResponse,
  type MasterApprenticesRegions,
  type MasterApprenticesRepublic,
  type TMasterApprenticesStore,
} from '../models';

const httpApi = new HttpApi();

export const useMasterApprenticesStatStore = create<TMasterApprenticesStore>(
  (set) => ({
    masterApprenticesRepublicResponse: null,
    masterApprenticesRegionResponse: null,
    masterApprenticesDistrictResponse: null,
    masterApprenticesRepublic: [],
    masterApprenticesRepublicCachedAt: null,
    masterApprenticesRegions: [],
    masterApprenticesRegionsHeader: null,
    masterApprenticesDistrictHeader: null,
    masterApprentices: [],
    isMasterApprenticesRepublicLoading: false,
    isMasterApprenticesRegionLoading: false,
    isMasterApprenticesLoading: false,

    fetchMasterApprenticesRepublicRates: async (
      params?: Record<string, any>,
    ) => {
      set({ isMasterApprenticesRepublicLoading: true });
      const res = await httpApi.get<any>(
        ENDPOINT_MASTER_APPRENTICES_RATES,
        cleanObject(params!),
      );

      if (res && Array.isArray(res.rows)) {
        res.rows = [...res.rows].sort((a, b) => {
          const indexA = REAGION_SOATO.findIndex((item) => item.id === a.region_id);
          const indexB = REAGION_SOATO.findIndex((item) => item.id === b.region_id);
          return indexA - indexB;
        });
      }

      set({
        masterApprenticesRepublicResponse: res,
      });
      setTimeout(() => {
        set({ isMasterApprenticesRepublicLoading: false });
      }, 300);
    },
    fetchMasterApprenticesRegionRates: async (params?: Record<string, any>) => {
      set({ isMasterApprenticesRepublicLoading: true });
      const res = await httpApi.get<MasterApprenticesRepublicResponse>(
        ENDPOINT_MASTER_APPRENTICES_REGION_RATES,
        cleanObject(params!),
      );
      set({
        masterApprenticesRegionResponse: res,
      });
      setTimeout(() => {
        set({ isMasterApprenticesRegionLoading: false });
      }, 300);
    },
    fetchMasterApprenticesDistrictRates: async (
      params?: Record<string, any>,
    ) => {
      set({ isMasterApprenticesRepublicLoading: true });
      const res = await httpApi.get<MasterApprenticesRepublicResponse>(
        ENDPOINT_MASTER_APPRENTICES_DISTRICT_RATES,
        cleanObject(params!),
      );
      set({
        masterApprenticesDistrictResponse: res,
      });
      setTimeout(() => {
        set({ isMasterApprenticesRegionLoading: false });
      }, 300);
    },
    fetchMasterApprenticesRepublic: async () => {
      set({ isMasterApprenticesRepublicLoading: true });
      const res = await httpApi.get<{
        cachedAt: string;
        data: MasterApprenticesRepublic[];
      }>(ENDPOINT_MASTER_APPRENTICES_REPUBLIC, {});

      const sortedData = Array.isArray(res?.data)
        ? [...res.data].sort((a, b) => {
            const indexA = REAGION_SOATO.findIndex((item) => item.id === a.region_id);
            const indexB = REAGION_SOATO.findIndex((item) => item.id === b.region_id);
            return indexA - indexB;
          })
        : [];

      set({
        masterApprenticesRepublic: sortedData.map((item, index) => ({
          id: index + 1,
          ...item,
        })),
        masterApprenticesRepublicCachedAt: res?.cachedAt || null,
      });
      setTimeout(() => {
        set({ isMasterApprenticesRepublicLoading: false });
      }, 300);
    },
    fetchMasterApprenticesRegions: async (region_id) => {
      set({
        isMasterApprenticesRegionLoading: true,
      });
      const res = await httpApi.get<
        MasterApprenticesRegionResponse<MasterApprenticesRegions[]>
      >(ENDPOINT_MASTER_APPRENTICES_REGION, {
        region_id,
      });
      set({
        masterApprenticesRegions: res?.data.map((item, index) => ({
          id: index + 1,
          ...item,
        })),
        masterApprenticesRegionsHeader: {
          region_id: res?.region_id,
          region_name: res?.region_name,
        },
      });
      setTimeout(() => {
        set({ isMasterApprenticesRegionLoading: false });
      }, 300);
    },
    fetchMasterApprentices: async (district_id) => {
      set({
        isMasterApprenticesLoading: true,
      });
      const res = await httpApi.get<
        MasterApprenticesDistrictResponse<MasterApprentices[]>
      >(ENDPOINT_MASTER_APPRENTICES_DISTRICT, {
        district_id,
      });
      set({
        masterApprentices: res?.data?.map((item, index) => ({
          id: index + 1,
          ...item,
        })),
        masterApprenticesDistrictHeader: {
          region_id: res?.region_id,
          region_name: res?.region_name,
          district_id: res?.district_id,
          district_name: res?.district_name,
        },
      });
      setTimeout(() => {
        set({ isMasterApprenticesLoading: false });
      }, 300);
    },
  }),
);
